import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useApiNoSqlContext } from 'src/modules/contexts/ApiNoSqlContext';
import {
	ApiError,
	InvoiceCategoriesEnum,
	InvoiceMapping,
	Prompt,
	PromptBase,
	PromptOptional,
	PromptUpdate
} from 'src/modules/generated-api-clients/no-sql';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { AxiosError } from 'axios';
import { MUTATION_KEYS, QUERY_KEYS } from './enums';
import { logApiError, logAxiosError, logError, logRequest } from './helpers';

interface ReadSinglePromptParams {
	id?: string;
	utilityId?: string;
	updatedDate?: string;
	question?: string;
	entity?: InvoiceMapping;
	returnType?: string;
	isActive?: boolean;
	version?: number;
	invoiceCategory?: InvoiceCategoriesEnum | undefined;
	utilityType?: string;
	project?: string;
}
interface ReadAllPromptsParams extends ReadSinglePromptParams {
	skip?: number;
	limit?: number;
}
interface PromptUpdateParams {
	id: string;
	requestBody: PromptUpdate;
}
interface PromptsUpdateParams {
	requestBody: PromptUpdate;
	id?: string;
	utilityId?: string;
	updatedDate?: string;
	question?: string;
	entity?: InvoiceMapping;
	returnType?: string;
	isActive?: boolean;
	version?: number;
	invoiceCategory?: InvoiceCategoriesEnum;
	utilityType?: string;
}
/**
 * Custom hook for fetching a single prompt.
 * Makes a GET request to '/api/v2/read/single/prompt' endpoint.
 *
 * @param params - An object containing the following properties:
 *   - id: The ID of the prompt (optional).
 *   - utilityId: The utility ID (optional).
 *   - updatedDate: The updated date of the prompt (optional).
 *   - question: The question associated with the prompt (optional).
 *   - entity: The entity related to the prompt (optional).
 *   - returnType: The return type of the prompt (optional).
 *   - isActive: Boolean indicating if the prompt is active (optional).
 *   - version: The version number of the prompt (optional).
 *   - invoiceCategory: The invoice category of the prompt, if applicable (optional).
 *   - utilityType: The utility type related to the prompt (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 *
 * @returns A query result object containing either a single PromptOptional or undefined, and an ApiError object in case of an error.
 */
export const useListPromptOptional = (params?: ReadAllPromptsParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	// const queryKey =
		// params === undefined ? [QUERY_KEYS.prompts.AllPrompts, user] : [QUERY_KEYS.prompts.AllPrompts, params, user];

		const queryKey =
		params === undefined 
		? [QUERY_KEYS.prompts.AllPrompts] 
		: [QUERY_KEYS.prompts.AllPrompts];
	const request = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.promptsPrompts.listPromptsOptionalOr404ApiV2ReadListPromptOptionalGet()
					: await client.promptsPrompts.listPromptsOptionalOr404ApiV2ReadListPromptOptionalGet(
						params.id,
						params.utilityId,
						params.updatedDate,
						params.question,
						params.entity,
						params.returnType,
						params.isActive,
						params.version,
						params.invoiceCategory,
						params.utilityType,
						params.project,
						params.skip,
						params.limit
					);
			// logRequest(params, 'GET', QUERY_KEYS.prompts.AllPrompts, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching prompt list');
			}
			throw error;
		}
	};
	return useQuery<PromptOptional[], ApiError>(queryKey, request, {
		enabled: !!user.attributes?.email
	});
};
export const useListPrompt = (params?: ReadAllPromptsParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryKey =
		params === undefined 
		? [QUERY_KEYS.prompts.AllPrompts] 
		: [QUERY_KEYS.prompts.AllPrompts, params];

	const request = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}

			const response =
				params === undefined
					? await client.promptsPrompts.listPromptsOr404ApiV2ReadListPromptGet()
					: await client.promptsPrompts.listPromptsOr404ApiV2ReadListPromptGet(
						params.utilityId,
						params.question,
						params.entity,
						params.returnType,
						params.isActive,
						params.version,
						params.invoiceCategory
					);
			// logRequest(params, 'GET', QUERY_KEYS.prompts.AllPrompts, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching prompt list');
			}
			throw error;
		}
	};
	return useQuery<Prompt[], ApiError>(
		queryKey,
		request,
		{
			enabled: !!user.attributes?.email
		}
	);
};

/**
 * Custom hook for fetching a single prompt.
 * Makes a GET request to '/api/v2/read/single/prompt' endpoint.
 *
 * @param params - An object containing the following properties:
 *   - id: The ID of the prompt (optional).
 *   - utilityId: The utility ID (optional).
 *   - updatedDate: The updated date of the prompt (optional).
 *   - question: The question associated with the prompt (optional).
 *   - entity: The entity related to the prompt (optional).
 *   - returnType: The return type of the prompt (optional).
 *   - isActive: Boolean indicating if the prompt is active (optional).
 *   - version: The version number of the prompt (optional).
 *   - invoiceCategory: The invoice category of the prompt, if applicable (optional).
 *   - utilityType: The utility type related to the prompt (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 *
 * @returns A query result object containing either a single PromptOptional or undefined, and an ApiError object in case of an error.
 */
export const useReadPromptOptional = (params?: ReadSinglePromptParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	const queryKey =
		params === undefined ? [QUERY_KEYS.prompts.AllPrompts, user] : [QUERY_KEYS.prompts.AllPrompts, params, user];

	const request = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.promptsPrompts.readPromptOptionalOr404ApiV2ReadPromptOptionalGet()
					: await client.promptsPrompts.readPromptOptionalOr404ApiV2ReadPromptOptionalGet(
						params.id,
						params.utilityId,
						params.updatedDate,
						params.question,
						params.entity,
						params.returnType,
						params.isActive,
						params.version,
						params.invoiceCategory,
						params.utilityType,
						params.project
					);
			// logRequest(params, 'GET', QUERY_KEYS.prompts.AllPrompts, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching prompt');
			}
			throw error;
		}
	};
	return useQuery<PromptOptional, ApiError>(
		queryKey,
		request,
		{
			enabled: !!user.attributes?.email
		}
	);
};

/**
 * Custom hook for creating a prompt.
 * Endpoint: POST '/api/v2/create/prompt'
 * @returns The result of the mutation, containing the created prompt or undefined, and an error object if applicable.
 */
export const useCreatePromptNoSql = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryClient = useQueryClient();
	return useMutation<Prompt, ApiError, PromptBase>(
		async (newPrompt: PromptBase) => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				const resp = await client.promptsPrompts.createPromptOr404ApiV2CreatePromptPost(newPrompt);
				return resp;
			}
			catch (error: ApiError | AxiosError | any) {
				throw error;
			}
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
				queryClient.invalidateQueries(QUERY_KEYS.prompts.SinglePrompt);
			},
			onError: (e: ApiError | unknown) => {
				// const error = e as ApiError;
				// const msg = `${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${error.message}\nBODY: ${JSON.stringify(
				// 		error.body
				// 	)}`
				// console.log('error: ', msg);
				// console.log('status: ', error.body.status)
				// console.log('message: ', error.body.message)
				// console.log('error: ', error.body.error)
				return
			}
		}
	);
};

/**
 * Custom hook for updating a prompt.
 * Endpoint: PATCH '/api/v2/update/prompt/{id}'
 * @returns The result of the mutation, containing the updated prompt or undefined, and an error object if applicable.
 */
export const useUpdatePromptNoSql = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryClient = useQueryClient();
	return useMutation<any, ApiError, PromptUpdateParams>(
		async (params: PromptUpdateParams) => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				const resp = await client.promptsPrompts.updatePromptOr404ApiV2UpdatePromptIdPatch(
					params.id,
					params.requestBody
				);
				logRequest(params, 'PATCH', MUTATION_KEYS.prompts.UpdatePrompt, resp);
				return resp;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error updating prompt');
				}
				throw error;
			}
		},
		{
			onSuccess: (response) => {
				queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
				queryClient.invalidateQueries(QUERY_KEYS.prompts.SinglePrompt);
				return response
			}
		}
	);
};

/**
 * Custom hook for updating multiple prompts.
 * Endpoint: PATCH '/api/v2/update/prompts'
 * @returns The result of the mutation, containing the update result or undefined, and an error object if applicable.
 */
export const useUpdateNoSqlPrompts = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, PromptsUpdateParams>(
		async params => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}

				const resp = await client.promptsPrompts.updateManyPromptsApiV2UpdateManyPromptsPatch(
					params.requestBody,
					params.utilityId,
					params.question,
					params.entity,
					params.returnType,
					params.isActive,
					params.version,
					params.invoiceCategory
				);
				// logRequest(params, 'PATCH', MUTATION_KEYS.prompts.UpdatePrompts, response);
				return resp;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error updating multiple prompts');
				}
				throw error;
			}
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
			}
		}
	);
};
